import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import {
	useBuyCreditsMutation,
	usePreviewBuyCreditsMutation,
	PreviewBuyCreditsResponse,
} from "@/pages/Private/redux/subscription/subscription.api";

import { Button } from "../Button/Button";
import { ButtonColor } from "../Button/types";
import { Icon } from "../Icon/Icon";
import { Spinner } from "../Spinner/Spinner";

interface TopupCreditsModalProps {
	show: boolean;
	setShow: (show: boolean) => void;
	/**
	 * Open the Stripe checkout in a new tab and close this dialog, instead of leaving the page. The
	 * credit-confirm dialog uses it so the selection it was opened for survives a top-up (PREC-4263).
	 */
	checkoutInNewTab?: boolean;
}

const PRESET_AMOUNTS = [2000, 5000, 10000];
const MIN_CREDITS = 1000;
const MAX_CREDITS = 10000;
const DEBOUNCE_MS = 500;

export const TopupCreditsModal: FunctionComponent<TopupCreditsModalProps> = ({
	show,
	setShow,
	checkoutInNewTab = false,
}) => {
	const { t } = useTranslation();
	const [selectedAmount, setSelectedAmount] = useState<number>(5000);
	const [customAmount, setCustomAmount] = useState<string>("");
	const [useCustom, setUseCustom] = useState(false);
	const [preview, setPreview] = useState<PreviewBuyCreditsResponse | null>(null);
	const [previewError, setPreviewError] = useState<string | null>(null);

	const [buyCredits, { isLoading: isBuying }] = useBuyCreditsMutation();
	const [fetchPreview, { isLoading: isLoadingPreview }] = usePreviewBuyCreditsMutation();

	const creditsToPurchase = useMemo(() => {
		if (useCustom) {
			const parsed = parseInt(customAmount, 10);

			return isNaN(parsed) ? 0 : Math.min(MAX_CREDITS, Math.max(MIN_CREDITS, parsed));
		}

		return selectedAmount;
	}, [useCustom, customAmount, selectedAmount]);

	const isValidAmount = creditsToPurchase >= MIN_CREDITS && creditsToPurchase <= MAX_CREDITS;

	const loadPreview = useCallback(
		async (credits: number) => {
			setPreviewError(null);

			try {
				const result = await fetchPreview({ credits }).unwrap();

				setPreview(result);
			} catch (error) {
				setPreview(null);

				const err = error as { data?: { message?: string }; status?: number };

				if (err.status === 400 && err.data?.message) {
					setPreviewError(err.data.message);
				} else {
					setPreviewError(t("topupCredits.errors.generic"));
				}
			}
		},
		[fetchPreview, t]
	);

	// Fetch preview whenever credit amount changes (debounced for custom input)
	useEffect(() => {
		if (!show || !isValidAmount) {
			return;
		}

		const timer = setTimeout(
			() => {
				loadPreview(creditsToPurchase);
			},
			useCustom ? DEBOUNCE_MS : 0
		);

		return () => clearTimeout(timer);
	}, [creditsToPurchase, isValidAmount, show, useCustom, loadPreview]);

	const handleBuyCredits = async () => {
		if (!isValidAmount || !preview) {
			return;
		}

		// Opened before the request, while the click still counts as a user gesture for the popup
		// blocker; pointed at the checkout once its URL is known.
		const checkoutTab = checkoutInNewTab ? window.open("", "_blank") : null;

		try {
			const { sessionUrl } = await buyCredits({ credits: creditsToPurchase }).unwrap();

			if (checkoutTab) {
				checkoutTab.opener = null;
				checkoutTab.location.href = sessionUrl;
				setShow(false);
			} else {
				window.location.href = sessionUrl;
			}
		} catch (error) {
			checkoutTab?.close();
			console.error("Failed to create checkout session:", error);
		}
	};

	const handlePresetClick = (amount: number) => {
		setSelectedAmount(amount);
		setUseCustom(false);
		setCustomAmount("");
	};

	const handleCustomChange = (value: string) => {
		setCustomAmount(value);
		setUseCustom(true);
	};

	const formatCurrency = (amount: number) => {
		return `€${amount.toFixed(2)}`;
	};

	if (!show) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="flex flex-col bg-white rounded-xl w-[480px] max-w-[95%] shadow-xl">
				{/* Header */}
				<div className="flex items-center justify-between p-5 border-b border-border-light">
					<h2 className="text-smd font-semibold text-text">{t("topupCredits.title")}</h2>
					<button
						className="p-1 hover:bg-brand-white rounded-lg transition-colors"
						type="button"
						onClick={() => setShow(false)}
					>
						<Icon className="w-5 h-5 text-text-meta" icon={faXmark} />
					</button>
				</div>

				{/* Content */}
				<div className="p-5">
					<p className="text-sm text-text-meta mb-4">{t("topupCredits.description")}</p>

					{/* Preset amounts */}
					<div className="mb-4">
						<label className="text-sm font-medium text-text mb-2 block">
							{t("topupCredits.selectAmount")}
						</label>
						<div className="flex gap-2">
							{PRESET_AMOUNTS.map((amount) => (
								<button
									key={amount}
									className={`flex-1 py-2.5 px-3 rounded-lg border text-sm font-medium transition-colors ${
										!useCustom && selectedAmount === amount
											? "border-brand-dark bg-brand-dark/5 text-brand-dark"
											: "border-border-light hover:border-gray-300 text-text"
									}`}
									type="button"
									onClick={() => handlePresetClick(amount)}
								>
									{amount.toLocaleString("de-DE")}
								</button>
							))}
						</div>
					</div>

					{/* Custom amount input */}
					<div className="mb-5">
						<label className="text-sm font-medium text-text mb-2 block">
							{t("topupCredits.customAmount")}
						</label>
						<input
							className={`w-full px-3 py-2.5 border rounded-lg text-sm ${
								useCustom ? "border-brand-dark" : "border-border-light"
							} focus:outline-none focus:border-brand-dark`}
							max={MAX_CREDITS}
							min={MIN_CREDITS}
							placeholder={`${MIN_CREDITS.toLocaleString("de-DE")} - ${MAX_CREDITS.toLocaleString("de-DE")}`}
							type="number"
							value={customAmount}
							onChange={(e) => handleCustomChange(e.target.value)}
						/>
						<div className="text-xs text-text-meta mt-1">
							Min: {MIN_CREDITS.toLocaleString("de-DE")} · Max:{" "}
							{MAX_CREDITS.toLocaleString("de-DE")}
						</div>
					</div>

					{/* Error message */}
					{previewError && (
						<div className="bg-error-light rounded-lg p-3 mb-4">
							<p className="text-sm text-status-error">{previewError}</p>
						</div>
					)}

					{/* Price breakdown */}
					<div className="bg-brand-white rounded-lg p-4 mb-2">
						{isLoadingPreview ? (
							<div className="flex items-center justify-center py-3">
								<Spinner color="#005A6F" />
								<span className="ml-2 text-sm text-text-meta">
									{t("topupCredits.calculatingPrice")}
								</span>
							</div>
						) : preview ? (
							<>
								<div className="flex justify-between items-center mb-1">
									<span className="text-sm text-text-meta">{t("topupCredits.pricePerCredit")}</span>
									<span className="text-sm text-text">
										{formatCurrency(preview.pricePerCredit)}
									</span>
								</div>
								<div className="flex justify-between items-center mb-1">
									<span className="text-sm text-text-meta">{t("topupCredits.netPrice")}</span>
									<span className="text-sm text-text">{formatCurrency(preview.netAmount)}</span>
								</div>
								<div className="flex justify-between items-center mb-2">
									<span className="text-sm text-text-meta">
										{preview.taxAmount > 0 && preview.taxPercentage
											? t("topupCredits.vatWithPercentage", {
													percentage: preview.taxPercentage,
												})
											: t("topupCredits.vat")}
									</span>
									<span className="text-sm text-text">
										{preview.taxAmount === 0 && preview.taxPercentage === null
											? t("topupCredits.taxExempt")
											: formatCurrency(preview.taxAmount)}
									</span>
								</div>
								<div className="border-t border-border-light pt-2 flex justify-between items-center">
									<span className="text-sm font-semibold text-text">{t("topupCredits.total")}</span>
									<span className="text-smd font-bold text-text">
										{formatCurrency(preview.totalAmount)}
									</span>
								</div>
								<div className="text-xs text-text-meta mt-1 text-right">
									{t("topupCredits.forCredits", {
										credits: preview.credits.toLocaleString("de-DE"),
									})}
								</div>
							</>
						) : (
							!previewError && (
								<div className="text-sm text-text-meta text-center py-2">
									{t("topupCredits.selectAmountToSeePrice")}
								</div>
							)
						)}
					</div>
				</div>

				{/* Footer */}
				<div className="flex justify-end gap-3 p-5 border-t border-border-light">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						title={t("basics.cancel")}
						onClick={() => setShow(false)}
					/>
					<Button
						color={ButtonColor.ACTION}
						disabled={!isValidAmount || isBuying || !preview || isLoadingPreview}
						isLoading={isBuying}
						title={t("topupCredits.buyButton", {
							credits: creditsToPurchase.toLocaleString("de-DE"),
						})}
						onClick={handleBuyCredits}
					/>
				</div>
			</div>
		</div>
	);
};
